import { Checkbox, Label, Modal, Select } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '@/components';
import { toErrorMessage } from '@/modules/auth/utils';
import { useServices } from '@/modules/catalog/api';
import { useProfessionals } from '@/modules/schedule/api';
import { type CommissionRule, useCreateCommissionRule, useUpdateCommissionRule } from '../api';

const FORM_ID = 'commission-rule-form';

const ruleFormSchema = z.object({
  professionalId: z.string(),
  serviceId: z.string(),
  percent: z.coerce.number().min(0, 'Informe o percentual').max(100),
  isActive: z.boolean(),
});

type RuleFormValues = z.infer<typeof ruleFormSchema>;

interface RuleDialogProps {
  open: boolean;
  rule?: CommissionRule | null;
  onClose: () => void;
}

/** Cadastro da regra: quanto o profissional ganha sobre o servico. */
export function RuleDialog({ open, rule, onClose }: RuleDialogProps) {
  const professionals = useProfessionals();
  const services = useServices({ page: 1, perPage: 100 });

  const createRule = useCreateCommissionRule();
  const updateRule = useUpdateCommissionRule();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RuleFormValues>({
    resolver: zodResolver(ruleFormSchema),
    defaultValues: { professionalId: '', serviceId: '', percent: 40, isActive: true },
  });

  useEffect(() => {
    if (!open) return;

    reset({
      professionalId: rule?.professionalId ?? '',
      serviceId: rule?.serviceId ?? '',
      percent: rule?.percent ?? 40,
      isActive: rule?.isActive ?? true,
    });
  }, [open, rule, reset]);

  function onSubmit(values: RuleFormValues) {
    // Vazio quer dizer "vale para todos": a API guarda nulo.
    const input = {
      professionalId: values.professionalId || undefined,
      serviceId: values.serviceId || undefined,
      percent: values.percent,
      isActive: values.isActive,
    };

    if (rule) {
      updateRule.mutate(
        {
          id: rule.id,
          input: { ...input, professionalId: values.professionalId, serviceId: values.serviceId },
        },
        { onSuccess: onClose },
      );
      return;
    }

    createRule.mutate(input, { onSuccess: onClose });
  }

  const error = createRule.error ?? updateRule.error;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={rule ? 'Editar regra' : 'Nova regra de comissao'}
      actions={[
        { label: 'Cancelar', variant: 'ghost', onClick: onClose },
        {
          label: rule ? 'Salvar' : 'Criar',
          type: 'submit',
          formId: FORM_ID,
          loading: createRule.isPending || updateRule.isPending,
        },
      ]}
    >
      <form id={FORM_ID} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
        {error ? <p className="text-sm text-destructive">{toErrorMessage(error)}</p> : null}

        <div className="space-y-2">
          <Label htmlFor="professionalId">Profissional</Label>
          <Select id="professionalId" {...register('professionalId')}>
            <option value="">Todos os profissionais</option>
            {(professionals.data?.data ?? []).map((professional) => (
              <option key={professional.id} value={professional.id}>
                {professional.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="serviceId">Servico</Label>
          <Select id="serviceId" {...register('serviceId')}>
            <option value="">Todos os servicos</option>
            {(services.data?.data ?? []).map((service) => (
              <option key={service.id} value={service.id}>
                {service.name}
              </option>
            ))}
          </Select>
        </div>

        <FormField
          label="Comissao (%)"
          type="number"
          step="0.01"
          min="0"
          max="100"
          error={errors.percent?.message}
          {...register('percent')}
        />

        <Checkbox label="Regra ativa" {...register('isActive')} />
      </form>
    </Modal>
  );
}
